// ─── App-icon badge: the red dot with a number, on every install ────────────
// Haley, 2026-09-10: "make it so I get the red dot on the app when I have
// unreads." One server truth (get_my_unread_badge: places with unreads —
// channels, user DMs, character DMs) so the Mac PWA, the iPhone PWA and the
// native app show the same number:
//   • the open page sets navigator.setAppBadge / clearAppBadge (Badging API:
//     installed PWAs on macOS/iOS 16.4+, Chrome desktop/Android);
//   • a closed PWA gets the count on every push (service worker, sw.js);
//   • the native iOS app gets aps.badge on every alert, and — because it has
//     no badge plugin to clear its own icon — a badge-only push from here
//     whenever the count changes while the app is open.
// Any read marker write or unread arrival calls notifyUnreadChanged(); the
// sync is debounced so a burst of reads costs one RPC.
import { supabase } from './supabaseClient.js';
import { sendPushRaw } from './pushUtils.js';

export const UNREAD_EVENT = 'mfml:unread-changed';
let timer = null;
let lastSynced = null;
let nativeFlag = null;

export function notifyUnreadChanged() {
  try { window.dispatchEvent(new CustomEvent(UNREAD_EVENT)); } catch { /* no window */ }
}

async function isNativeApp() {
  if (nativeFlag !== null) return nativeFlag;
  try {
    const core = await import('@capacitor/core');
    nativeFlag = !!core?.Capacitor?.isNativePlatform?.();
  } catch { nativeFlag = false; }
  return nativeFlag;
}

export async function syncAppBadge() {
  try {
    // Signed out: nothing to count, and the RPC would only answer 401.
    const { data: { session } = { session: null } } = await supabase.auth.getSession().catch(() => ({ data: { session: null } }));
    if (!session) { try { await navigator.clearAppBadge?.(); } catch { /* unsupported */ } return null; }
    const { data, error } = await supabase.rpc('get_my_unread_badge');
    if (error) return null;
    const n = Math.max(0, Number(data) || 0);
    try {
      if (n > 0) await navigator.setAppBadge?.(n);
      else await navigator.clearAppBadge?.();
    } catch { /* not installed / unsupported */ }
    if (n !== lastSynced && await isNativeApp()) {
      sendPushRaw({ badge_only: true }).catch(() => {});
    }
    lastSynced = n;
    return n;
  } catch { return null; }
}

export function scheduleBadgeSync(delayMs = 1500) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { timer = null; syncAppBadge(); }, delayMs);
}

// Mount once per signed-in session; returns the teardown.
export function installBadgeSync() {
  const onChanged = () => scheduleBadgeSync();
  const onVisible = () => { if (document.visibilityState === 'visible') scheduleBadgeSync(300); };
  window.addEventListener(UNREAD_EVENT, onChanged);
  document.addEventListener('visibilitychange', onVisible);
  scheduleBadgeSync(2000);
  return () => {
    window.removeEventListener(UNREAD_EVENT, onChanged);
    document.removeEventListener('visibilitychange', onVisible);
    if (timer) { clearTimeout(timer); timer = null; }
  };
}
